import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getConsentBySession, saveConsentRecord, saveDataRequest } from '../../services/firestore/dataopsService';
import { getSessionId } from '../../utils/sessionId';
import { logAudit } from '../../utils/auditLog';
import { getProfile, recordDeletionRequest, cancelDeletionRequest } from '../../services/firestore/profileService';
import type { ConsentRecord, ConsentPurpose } from '../../types/dataops';

const PURPOSE_LABELS: Record<ConsentPurpose, { label: string; description: string; required: boolean }> = {
  lab_data_collection: {
    label: 'Lab Data Collection & Analysis',
    description: 'Allow HemoLogix to collect and analyze your uploaded lab results using AI.',
    required: true,
  },
  health_profile: {
    label: 'Health Profile Processing',
    description: 'Allow HemoLogix to process your age, sex, and health information to personalize content.',
    required: true,
  },
  supplement_recommendations: {
    label: 'Supplement Protocol Suggestions',
    description: 'Allow HemoLogix to suggest supplement protocols based on your lab data.',
    required: false,
  },
  provider_sharing: {
    label: 'Share Data with Healthcare Providers',
    description: 'Allow sharing your health information with providers you connect through our platform.',
    required: false,
  },
  pharmacy_sharing: {
    label: 'Share Data with Pharmacy Partners',
    description: 'Allow sharing relevant information with compounding pharmacy partners for order fulfillment.',
    required: false,
  },
  marketing_email: {
    label: 'Marketing Communications',
    description: 'Receive educational content, product updates, and promotional offers via email.',
    required: false,
  },
  analytics: {
    label: 'Usage Analytics',
    description: 'Allow collection of anonymized usage data to improve the platform.',
    required: false,
  },
  openloop_telehealth: {
    label: 'Telehealth Services',
    description: 'Allow HemoLogix to facilitate telehealth consultations with licensed prescribers through our provider network.',
    required: false,
  },
  openloop_phi_sharing: {
    label: 'Share PHI with Provider Network',
    description: 'Allow sharing your protected health information with our physician network for prescribing and clinical review.',
    required: false,
  },
  longitudinal_data_retention: {
    label: 'Longitudinal Data Retention',
    description: 'Allow HemoLogix to retain your health data over time to track trends and improvements.',
    required: false,
  },
};

type ConsentStatus = Record<ConsentPurpose, 'granted' | 'withdrawn' | 'none'>;

const CORRECTABLE_FIELDS = [
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'birthdate', label: 'Date of Birth' },
  { value: 'sex', label: 'Biological Sex' },
  { value: 'medications', label: 'Medications' },
  { value: 'conditions', label: 'Health Conditions' },
  { value: 'allergies', label: 'Allergies' },
];

export function PrivacySettings() {
  const [loading, setLoading] = useState(true);
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>({} as ConsentStatus);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [requestSent, setRequestSent] = useState<string | null>(null);
  // Correction form state
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [correctionField, setCorrectionField] = useState('');
  const [correctionOldValue, setCorrectionOldValue] = useState('');
  const [correctionNewValue, setCorrectionNewValue] = useState('');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  // Deletion cooling-off state
  const [deletionPending, setDeletionPending] = useState(false);
  const [deletionScheduledAt, setDeletionScheduledAt] = useState<string | null>(null);
  const [deletionCountdown, setDeletionCountdown] = useState('');
  const [cancellingDeletion, setCancellingDeletion] = useState(false);
  const sessionId = getSessionId();

  // Check for pending deletion on mount
  const checkDeletionStatus = useCallback(async () => {
    try {
      const profile = await getProfile(sessionId);
      if (profile?.deletionRequestedAt && profile.deletionScheduledAt) {
        const scheduled = new Date(profile.deletionScheduledAt).getTime();
        if (scheduled > Date.now()) {
          setDeletionPending(true);
          setDeletionScheduledAt(profile.deletionScheduledAt);
        }
      }
    } catch {
      // Profile may not exist yet
    }
  }, [sessionId]);

  useEffect(() => {
    (async () => {
      try {
        const records = await getConsentBySession(sessionId);
        const status: ConsentStatus = {} as ConsentStatus;
        for (const purpose of Object.keys(PURPOSE_LABELS) as ConsentPurpose[]) {
          const latest = records.find((r) => r.purpose === purpose);
          status[purpose] = latest ? latest.action : 'none';
        }
        setConsentStatus(status);
        await checkDeletionStatus();
      } catch (err) {
        console.error('Failed to load consent records:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId, checkDeletionStatus]);

  // Countdown timer for deletion cooling-off
  useEffect(() => {
    if (!deletionScheduledAt) return;
    const update = () => {
      const remaining = new Date(deletionScheduledAt).getTime() - Date.now();
      if (remaining <= 0) {
        setDeletionCountdown('Processing...');
        return;
      }
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      setDeletionCountdown(`${hours}h ${minutes}m remaining`);
    };
    update();
    const interval = setInterval(update, 60_000);
    return () => clearInterval(interval);
  }, [deletionScheduledAt]);

  async function handleWithdraw(purpose: ConsentPurpose) {
    const now = new Date().toISOString();
    const record: ConsentRecord = {
      id: `consent-${sessionId.slice(0, 8)}-${purpose}-${Date.now()}`,
      userId: sessionId,
      purpose,
      action: 'withdrawn',
      policyVersion: '1.0.0',
      consentText: PURPOSE_LABELS[purpose].description,
      method: 'toggle',
      ipAddress: null,
      userAgent: navigator.userAgent,
      timestamp: now,
      expiresAt: null,
      withdrawalReason: null,
      effectiveImmediately: true,
    };

    try {
      await saveConsentRecord(record);
      logAudit('consent.withdrawn', purpose, { method: 'toggle' });
      setConsentStatus((prev) => ({ ...prev, [purpose]: 'withdrawn' }));
    } catch (err) {
      console.error('Failed to withdraw consent:', err);
    }
  }

  async function handleGrant(purpose: ConsentPurpose) {
    const now = new Date().toISOString();
    const record: ConsentRecord = {
      id: `consent-${sessionId.slice(0, 8)}-${purpose}-${Date.now()}`,
      userId: sessionId,
      purpose,
      action: 'granted',
      policyVersion: '1.0.0',
      consentText: PURPOSE_LABELS[purpose].description,
      method: 'toggle',
      ipAddress: null,
      userAgent: navigator.userAgent,
      timestamp: now,
      expiresAt: null,
      withdrawalReason: null,
      effectiveImmediately: true,
    };

    try {
      await saveConsentRecord(record);
      logAudit('consent.granted', purpose, { method: 'toggle' });
      setConsentStatus((prev) => ({ ...prev, [purpose]: 'granted' }));
    } catch (err) {
      console.error('Failed to grant consent:', err);
    }
  }

  async function handleDataRequest(type: 'access' | 'deletion') {
    const now = new Date().toISOString();
    const deadline = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    const id = `req-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    try {
      await saveDataRequest({
        id,
        userId: sessionId,
        userEmail: '',
        type,
        status: 'received',
        receivedAt: now,
        source: 'user_settings',
        verificationMethod: 'account_auth',
        verifiedAt: null,
        assignedTo: 'system',
        startedAt: null,
        deletionManifest: null,
        exportPackage: null,
        corrections: null,
        completedAt: null,
        completionProof: null,
        denialReason: null,
        slaDeadline: deadline,
        slaDaysRemaining: 30,
        slaBreached: false,
        notes: '',
        auditTrail: [{
          action: `${type}_request_created`,
          performedBy: sessionId,
          timestamp: now,
          details: `User requested data ${type} via privacy settings`,
        }],
      });

      logAudit(type === 'deletion' ? 'data.delete' : 'data.export', 'privacy_settings', { request_id: id });
      setRequestSent(type === 'deletion' ? 'deletion' : 'access');
      setShowDeleteConfirm(false);
      setDeleteInput('');

      // If deletion, also record in profile for cooling-off tracking
      if (type === 'deletion') {
        await recordDeletionRequest(sessionId);
        await checkDeletionStatus();
      }
    } catch (err) {
      console.error('Failed to create data request:', err);
    }
  }

  async function handleCorrectionRequest() {
    if (!correctionField || !correctionNewValue.trim()) return;
    setCorrectionSubmitting(true);
    const now = new Date().toISOString();
    const deadline = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    const id = `req-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    try {
      await saveDataRequest({
        id,
        userId: sessionId,
        userEmail: '',
        type: 'correction',
        status: 'received',
        receivedAt: now,
        source: 'user_settings',
        verificationMethod: 'account_auth',
        verifiedAt: null,
        assignedTo: 'system',
        startedAt: null,
        deletionManifest: null,
        exportPackage: null,
        corrections: [{
          field: correctionField,
          oldValue: correctionOldValue,
          newValue: correctionNewValue,
          appliedAt: '',
        }],
        completedAt: null,
        completionProof: null,
        denialReason: null,
        slaDeadline: deadline,
        slaDaysRemaining: 30,
        slaBreached: false,
        notes: '',
        auditTrail: [{
          action: 'correction_request_created',
          performedBy: sessionId,
          timestamp: now,
          details: `User requested correction to ${correctionField}`,
        }],
      });

      logAudit('data.modify', 'privacy_settings', { request_id: id, field: correctionField });
      setRequestSent('correction');
      setShowCorrectionForm(false);
      setCorrectionField('');
      setCorrectionOldValue('');
      setCorrectionNewValue('');
    } catch (err) {
      console.error('Failed to create correction request:', err);
    } finally {
      setCorrectionSubmitting(false);
    }
  }

  async function handleCancelDeletion() {
    setCancellingDeletion(true);
    try {
      await cancelDeletionRequest(sessionId);
      logAudit('data.modify', 'privacy_settings', { action: 'deletion_cancelled' });
      setDeletionPending(false);
      setDeletionScheduledAt(null);
      setRequestSent(null);
    } catch (err) {
      console.error('Failed to cancel deletion:', err);
    } finally {
      setCancellingDeletion(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-deep text-text-primary flex items-center justify-center">
        <p className="text-text-muted text-lg">Loading privacy settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-deep text-text-primary font-sans">
      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Link to="/" className="text-text-muted hover:text-text-primary transition-colors text-sm">
            &larr; Back
          </Link>
        </div>
        <h1 className="font-display text-3xl font-bold text-gold mb-2">Privacy & Data</h1>
        <p className="text-text-muted text-base mb-8">
          Manage your consent preferences and data rights.
        </p>

        {/* Request confirmation */}
        {requestSent && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-900/20 border border-green-800/50 rounded-xl p-4 mb-6"
          >
            <p className="text-optimal font-medium">
              {requestSent === 'deletion' ? 'Deletion request submitted' :
               requestSent === 'correction' ? 'Correction request submitted' :
               'Data export request submitted'}
            </p>
            <p className="text-text-muted text-sm mt-1">
              {requestSent === 'deletion'
                ? 'A 24-hour cooling-off period applies. You can cancel below before processing begins.'
                : 'We will process your request within 30 days as required by law.'}
            </p>
          </motion.div>
        )}

        {/* Consent Management */}
        <section className="mb-10">
          <h2 className="text-xl font-display font-semibold mb-4">Consent Preferences</h2>
          <div className="space-y-3">
            {(Object.entries(PURPOSE_LABELS) as [ConsentPurpose, typeof PURPOSE_LABELS[ConsentPurpose]][]).map(
              ([purpose, info]) => {
                const status = consentStatus[purpose] || 'none';
                const isActive = status === 'granted';

                return (
                  <div
                    key={purpose}
                    className={`p-4 rounded-xl border transition-all ${
                      isActive
                        ? 'bg-bg-card border-bg-tertiary'
                        : 'bg-bg-deep border-bg-tertiary/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-base">{info.label}</span>
                          {info.required && (
                            <span className="text-xs font-semibold text-accent-light bg-accent/10 px-2 py-0.5 rounded-full">
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-text-muted text-sm leading-relaxed">{info.description}</p>
                      </div>
                      <div className="flex-shrink-0 pt-1">
                        {info.required ? (
                          <span className="text-xs text-text-muted">Always on</span>
                        ) : (
                          <button
                            onClick={() => isActive ? handleWithdraw(purpose) : handleGrant(purpose)}
                            className={`relative w-12 h-7 rounded-full transition-colors ${
                              isActive ? 'bg-optimal' : 'bg-bg-tertiary'
                            }`}
                          >
                            <span
                              className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                                isActive ? 'left-6' : 'left-1'
                              }`}
                            />
                          </button>
                        )}
                      </div>
                    </div>
                    {status !== 'none' && (
                      <p className="text-xs text-text-muted mt-2">
                        Status: {status === 'granted' ? 'Active' : 'Withdrawn'}
                      </p>
                    )}
                  </div>
                );
              },
            )}
          </div>
        </section>

        {/* Data Rights */}
        <section className="mb-10">
          <h2 className="text-xl font-display font-semibold mb-4">Your Data Rights</h2>
          <div className="space-y-3">
            {/* Download Data */}
            <div className="p-4 rounded-xl bg-bg-card border border-bg-tertiary">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-base">Download My Data</p>
                  <p className="text-text-muted text-sm mt-1">
                    Request a copy of all your data in a downloadable format. We will process your request within 30 days.
                  </p>
                </div>
                <button
                  onClick={() => handleDataRequest('access')}
                  disabled={requestSent === 'access'}
                  className="px-4 py-2 rounded-lg bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary text-sm font-medium transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  Request
                </button>
              </div>
            </div>

            {/* Request Correction */}
            <div className="p-4 rounded-xl bg-bg-card border border-bg-tertiary">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-base">Request Data Correction</p>
                  <p className="text-text-muted text-sm mt-1">
                    Request a correction to your personal data. We will review and process within 30 days.
                  </p>
                </div>
                <button
                  onClick={() => setShowCorrectionForm(!showCorrectionForm)}
                  disabled={requestSent === 'correction'}
                  className="px-4 py-2 rounded-lg bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary text-sm font-medium transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  {showCorrectionForm ? 'Cancel' : 'Correct'}
                </button>
              </div>
              {showCorrectionForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 space-y-3"
                >
                  <div>
                    <label className="text-sm text-text-muted block mb-1">Field to correct</label>
                    <select
                      value={correctionField}
                      onChange={(e) => setCorrectionField(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-bg-deep border border-bg-tertiary text-text-primary text-base focus:border-accent/50 focus:outline-none"
                    >
                      <option value="">Select a field...</option>
                      {CORRECTABLE_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-text-muted block mb-1">Current value (optional)</label>
                    <input
                      type="text"
                      value={correctionOldValue}
                      onChange={(e) => setCorrectionOldValue(e.target.value)}
                      placeholder="What it currently shows"
                      className="w-full px-4 py-3 rounded-lg bg-bg-deep border border-bg-tertiary text-text-primary text-base focus:border-accent/50 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-text-muted block mb-1">Corrected value</label>
                    <input
                      type="text"
                      value={correctionNewValue}
                      onChange={(e) => setCorrectionNewValue(e.target.value)}
                      placeholder="What it should be"
                      className="w-full px-4 py-3 rounded-lg bg-bg-deep border border-bg-tertiary text-text-primary text-base focus:border-accent/50 focus:outline-none"
                    />
                  </div>
                  <button
                    onClick={handleCorrectionRequest}
                    disabled={!correctionField || !correctionNewValue.trim() || correctionSubmitting}
                    className="w-full py-3 rounded-lg bg-accent text-white font-medium transition-colors hover:bg-accent-light disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {correctionSubmitting ? 'Submitting...' : 'Submit Correction Request'}
                  </button>
                </motion.div>
              )}
            </div>

            {/* Delete Data */}
            <div className="p-4 rounded-xl bg-bg-card border border-bg-tertiary">
              {deletionPending ? (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-base text-amber-400">Deletion Pending</p>
                      <p className="text-text-muted text-sm mt-1">
                        Your data deletion request is in the 24-hour cooling-off period. Processing begins after the cooling-off period ends. You can cancel this request before then.
                      </p>
                      <p className="text-amber-400 text-sm font-medium mt-2">
                        {deletionCountdown}
                      </p>
                    </div>
                    <button
                      onClick={handleCancelDeletion}
                      disabled={cancellingDeletion}
                      className="px-4 py-2 rounded-lg bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary text-sm font-medium transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {cancellingDeletion ? 'Cancelling...' : 'Cancel Deletion'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-base">Delete My Account & Data</p>
                    <p className="text-text-muted text-sm mt-1">
                      Permanently delete all your data including lab results, AI analyses, and chat history. A 24-hour cooling-off period applies before processing begins.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={requestSent === 'deletion'}
                    className="px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 text-sm font-medium transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-bg-card rounded-2xl p-6 max-w-md w-full border border-bg-tertiary"
            >
              <h3 className="text-lg font-display font-bold text-red-400 mb-3">Delete All Data?</h3>
              <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                This will permanently delete all your data including lab results, AI analyses, chat history, and health profile.
                This action cannot be undone. A 24-hour cooling-off period applies before processing begins.
              </p>
              <p className="text-text-muted text-sm mb-4">
                Type <strong className="text-text-primary">DELETE</strong> to confirm:
              </p>
              <input
                type="text"
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="Type DELETE"
                className="w-full px-4 py-3 rounded-lg bg-bg-deep border border-bg-tertiary text-text-primary text-base mb-4 focus:border-red-500/50 focus:outline-none"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); }}
                  className="flex-1 py-3 rounded-lg bg-bg-elevated border border-bg-tertiary text-text-primary font-medium transition-colors hover:bg-bg-tertiary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDataRequest('deletion')}
                  disabled={deleteInput !== 'DELETE'}
                  className="flex-1 py-3 rounded-lg bg-red-500 text-white font-medium transition-colors hover:bg-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Confirm Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Legal links */}
        <section className="border-t border-bg-tertiary pt-6">
          <div className="flex flex-wrap gap-4 text-sm text-text-muted">
            <a href="/privacy" className="hover:text-text-primary transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-text-primary transition-colors">Terms of Service</a>
            <a href="mailto:support@hemologix.com" className="hover:text-text-primary transition-colors">Contact Support</a>
          </div>
          <p className="text-xs text-text-muted mt-4">
            Session ID: {sessionId.slice(0, 12)}... &middot; Policy version 1.0.0
          </p>
        </section>
      </div>
    </div>
  );
}
